import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";

const BlueBar = ({ children }) => (
  <div className="bg-[#1e3a8a] border border-[#3b5bd0]/40 rounded-t-lg px-3 py-1.5 text-center text-[11px] sm:text-xs font-black uppercase tracking-widest text-white">{children}</div>
);

function DepthGrid({ label, slots, pos }) {
  if (!slots || slots.length === 0) return null;
  return (
    <div>
      <BlueBar>{label}</BlueBar>
      <div className={`grid border border-t-0 border-white/10 rounded-b-lg overflow-hidden`} style={{ gridTemplateColumns: `repeat(${slots.length}, minmax(0,1fr))` }}>
        {slots.map((p, i) => (
          <div key={i} className="border-r border-white/10 last:border-r-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-center py-1 bg-black/60 text-neutral-300 border-b border-white/10">{pos}{i + 1}</div>
            <div className="bg-[#1e3a8a]/60 text-center text-[11px] font-bold text-white px-1 py-1 truncate" title={p.name}>{p.name}</div>
            <div className="text-center text-[10px] font-data text-neutral-400 py-0.5">{p.cls} | {p.ht || "—"} {p.wt || ""}</div>
            <div className={`text-center text-[9px] font-black uppercase tracking-widest py-0.5 ${p.cls?.includes("FR") ? "bg-neutral-600 text-white" : "bg-[#16a34a] text-black"}`}>
              {p.cls?.includes("FR") ? "Freshman" : "Returning"}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const rankCls = (r, n = 136) => (r == null ? "bg-white/5 text-neutral-500" : r <= n * 0.27 ? "bg-emerald-500/25 text-emerald-300" : r <= n * 0.66 ? "bg-yellow-500/10 text-yellow-100" : "bg-red-500/25 text-red-300");

function UnitBox({ label, u, n }) {
  if (!u) return null;
  const cols = [["PPA", u.ppa, u.ppa_rank], ["SR%", u.sr, u.sr_rank], ["EXPL", u.expl, u.expl_rank]];
  return (
    <div>
      <BlueBar>{label}</BlueBar>
      <div className="grid grid-cols-3 border border-t-0 border-white/10 rounded-b-lg overflow-hidden">
        {cols.map(([k]) => <div key={k} className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold text-center py-1 bg-black/50 border-b border-white/10">{k}</div>)}
        {cols.map(([k, v, r]) => (
          <div key={k + "v"} className={`text-center py-1.5 ${rankCls(r, n)}`}>
            <div className="font-data text-sm font-bold">{v}</div>
            <div className="text-[9px] font-data opacity-80">#{r ?? "—"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Side({ s }) {
  return (
    <div className="space-y-4 min-w-0" data-testid={`cfb-mlab-side-${s.abbrev}`}>
      <div className="card-surface rounded-lg p-3 flex items-center gap-3">
        {s.logo && <img src={s.logo} alt="" className="w-12 h-12 object-contain" loading="lazy" />}
        <div className="flex-1">
          <div className="font-display text-lg font-bold uppercase">{s.rank ? `#${s.rank} ` : ""}{s.team}</div>
          <div className="text-[11px] text-neutral-500 font-data">{s.record || ""}</div>
        </div>
        <div className="text-right">
          <div className="font-data text-2xl font-black gold-text" data-testid={`cfb-fpi-${s.abbrev}`}>{s.fpi != null ? `${s.fpi}%` : "—"}</div>
          <div className="text-[9px] uppercase tracking-widest text-neutral-600 font-bold">FPI Win Proj</div>
        </div>
      </div>

      <div className="card-surface rounded-lg p-3 space-y-3">
        <DepthGrid label={`${s.abbrev} Projected QBs`} slots={s.depth?.QB} pos="QB" />
        <DepthGrid label={`${s.abbrev} Projected RBs`} slots={s.depth?.RB} pos="RB" />
        <DepthGrid label={`${s.abbrev} Projected Receiving Weapons`} slots={s.depth?.WR} pos="WR" />
        <DepthGrid label={`${s.abbrev} Tight Ends`} slots={s.depth?.TE} pos="TE" />
        <p className="text-[9px] text-neutral-600">Slots ordered by class seniority (projected) — depth confirmed at kickoff.</p>
      </div>

      {s.units && (
        <div className="card-surface rounded-lg p-3 space-y-3" data-testid={`cfb-units-${s.abbrev}`}>
          <UnitBox label={`${s.abbrev} Offense (${s.units_year})`} u={s.units.off} n={s.units_n} />
          <UnitBox label={`${s.abbrev} Defense (${s.units_year})`} u={s.units.def} n={s.units_n} />
          <p className="text-[9px] text-neutral-600">PPA = predicted points added per play · SR% = success rate · EXPL = explosiveness. Ranks of {s.units_n} FBS teams (offense: high = good, defense: low = good). Garbage time excluded.</p>
        </div>
      )}

      {(s.last5 || []).length > 0 && (
        <div className="card-surface rounded-lg overflow-hidden">
          <BlueBar>Last 5 Games</BlueBar>
          <div className="flex flex-wrap gap-1.5 p-3">
            {s.last5.map((g, i) => (
              <span key={i} className={`text-[10px] font-data px-2 py-0.5 rounded-full border ${g.result === "W" ? "border-[#22c55e]/50 text-[#22c55e] bg-[#22c55e]/10" : "border-red-500/40 text-red-400 bg-red-500/10"}`}>
                {g.result} {g.score} vs {g.opp}
              </span>
            ))}
          </div>
        </div>
      )}

      {(s.leaders || []).length > 0 && (
        <div className="card-surface rounded-lg overflow-hidden">
          <BlueBar>{s.abbrev} Season Leaders</BlueBar>
          {s.leaders.slice(0, 4).map((l, i) => (
            <div key={i} className="flex items-center gap-2.5 px-3 py-2 border-b border-white/5 last:border-0">
              {l.headshot && <img src={l.headshot} alt="" className="w-8 h-8 rounded-full object-cover bg-white/5" loading="lazy" />}
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{l.name} <span className="text-neutral-600 text-[10px]">{l.pos}</span></div>
                <div className="text-[10px] text-neutral-500">{l.cat}</div>
              </div>
              <div className="font-data text-[11px] text-[#a855f7] text-right max-w-[45%] truncate">{l.line}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NCAAFMatchup() {
  const { data: board } = useQuery({ queryKey: ["ncaaf", "board"], queryFn: () => api.get("/ncaaf/board").then((r) => r.data) });
  const games = (board?.days || []).flatMap((d) => d.games).filter((g) => g.state === "pre").slice(0, 40);
  const [eventId, setEventId] = useState(null);
  useEffect(() => {
    if (!eventId && games.length > 0) setEventId(games[0].id);
  }, [games, eventId]);
  const { data: detail, isFetching } = useQuery({
    queryKey: ["cfbMatchup", eventId],
    queryFn: () => api.get("/ncaaf/matchup", { params: { event: eventId } }).then((r) => r.data),
    enabled: !!eventId, staleTime: 30 * 60 * 1000,
  });
  const sel = games.find((g) => g.id === eventId);
  return (
    <div className="space-y-4">
      <p className="text-neutral-500 text-sm">Pregame deep dive: ESPN FPI win projections, projected QB/RB/WR depth by class, recent form and season leaders.</p>
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="cfb-mlab-chips">
        {games.map((g) => (
          <button key={g.id} onClick={() => setEventId(g.id)}
            className={`shrink-0 text-xs font-bold uppercase tracking-wide px-3 py-2 rounded-lg border transition-colors duration-200 ${eventId === g.id ? "bg-[#1e3a8a] border-[#3b5bd0] text-white" : "border-white/15 text-neutral-400 hover:border-[#3b5bd0]/60 hover:text-white"}`}>
            {g.away.abbrev} @ {g.home.abbrev}
          </button>
        ))}
      </div>
      {sel && <div className="text-[11px] text-neutral-500">{sel.day_label} · {sel.time_et}{sel.tv ? ` · ${sel.tv}` : ""} · {sel.venue}</div>}
      {isFetching && !detail && <div className="text-neutral-500 font-data text-sm animate-pulse">Pulling the sheet...</div>}
      {detail && (
        <div className="grid lg:grid-cols-2 gap-5" data-testid="cfb-mlab-detail">
          {detail.sides.map((s) => <Side key={s.homeAway} s={s} />)}
        </div>
      )}
      <p className="text-[11px] text-neutral-600">FPI = ESPN's Football Power Index game projection. Season leader stats populate as games are played.</p>
    </div>
  );
}
